import { ReactNode, useState, useEffect } from 'react';
import * as Sentry from "@sentry/react";
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logging';

interface AuthGuardProps {
  children: ReactNode;
  requireAuth?: boolean;
  requireVerification?: boolean;
}

export function AuthGuard({ 
  children, 
  requireAuth = true,
  requireVerification = true 
}: AuthGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Add timeout for loading state to prevent infinite loading
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        logger.warn('AuthGuard loading timeout reached', { component: 'AuthGuard' });
        setLoadingTimeout(true);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [loading]);

  if (loading && !loadingTimeout) {
    logger.debug('AuthGuard showing loading spinner', { component: 'AuthGuard' });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-sm text-muted-foreground">Loading authentication...</p>
        </div>
      </div>
    );
  }

  if (loadingTimeout) {
    Sentry.captureException(new Error('[AuthGuard] Auth loading timeout'), {
      tags: { context: 'auth_error', component: 'AuthGuard' },
      extra: { message: 'Auth loading timeout, showing error' }
    });
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md">
          <h2 className="text-xl font-semibold text-destructive">Authentication Timeout</h2>
          <p className="text-sm text-muted-foreground">
            The authentication system is taking longer than expected. This might be a network issue.
          </p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (requireAuth && !user) {
    // Log as info - this is expected behavior, not an error
    logger.securityEvent('Protected route access blocked (user not authenticated)', {
      component: 'AuthGuard',
      attemptedPath: location.pathname
    });
    
    // Redirect to login with return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!requireAuth && user) {
    // Redirect authenticated users away from login/register pages
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}